import { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export const TTSPlayer = ({ 
    text, 
    onPlay, 
    onEnd,
    autoPlay = false,
    api,
    className 
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        if (autoPlay && text) {
            // Small delay to ensure component is mounted
            const timer = setTimeout(() => {
                playTTS();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [autoPlay]); // Only trigger when autoPlay changes to true

    const playTTS = async () => {
        if (!text || isLoading) return;

        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('text', text);
            
            // Override Content-Type to let axios set it correctly for FormData
            const response = await api.post('/tts', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            const audioBase64 = response.data.audio_base64;
            
            if (audioRef.current) {
                audioRef.current.src = `data:audio/mp3;base64,${audioBase64}`;
                try {
                    await audioRef.current.play();
                    setIsPlaying(true);
                    onPlay?.();
                } catch (playError) {
                    // Browser blocked autoplay - expected behavior, user needs to click play
                    setIsPlaying(false);
                }
            }
        } catch (error) {
            console.error('TTS failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const stopTTS = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        onEnd?.();
    };

    return (
        <div className={cn('inline-flex items-center gap-1', className)}>
            <audio 
                ref={audioRef} 
                onEnded={handleEnded}
                onPause={() => setIsPlaying(false)}
            />
            <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={isPlaying ? stopTTS : playTTS}
                disabled={isLoading}
            >
                {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : isPlaying ? (
                    <VolumeX className="h-4 w-4" />
                ) : (
                    <Volume2 className="h-4 w-4 opacity-60 hover:opacity-100" />
                )}
            </Button>
        </div>
    );
};
